/**
 * The contrast twins on the "Nästa steg" card (pedagogy spec §5.3.8, §13 Q13-Q14): one block per
 * twin with what it changes, the one-tap prediction and "Gör kontrastuppgiften". The prediction is
 * required before the twin is inserted: starting without a choice shows
 * `practice.prediction.required`, sends nothing and moves focus to the choices. A link that authors
 * no prediction has the button alone. Once inserted the block says so and recalls the prediction.
 */
import { GitCompareArrows } from "lucide-react";
import { createRef, useRef, useState, type RefObject } from "react";
import { Localized } from "@client/components/Localized";
import { Badge, Button } from "@client/components/ui";
import { useT } from "@client/i18n";
import type { ContrastOfferView } from "./followUp";
import { PredictionCard, PredictionChoice, type PredictionCardHandle } from "./PredictionCard";
import type { ContrastOffer } from "./types";

export interface ContrastOffersProps {
  views: readonly ContrastOfferView[];
  /** Insert the twin with the chosen prediction (`undefined` when the link authors none). */
  onStart: (offer: ContrastOffer, choice: string | undefined) => void;
}

export function ContrastOffers({ views, onStart }: ContrastOffersProps) {
  const t = useT();
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<Record<string, boolean>>({});
  const handles = useRef<Record<string, RefObject<PredictionCardHandle>>>({});

  function handleOf(id: string): RefObject<PredictionCardHandle> {
    return (handles.current[id] ??= createRef<PredictionCardHandle>());
  }

  function start(offer: ContrastOffer) {
    const choice = choices[offer.questionId];
    if (offer.prediction && choice === undefined) {
      setMissing((prev) => ({ ...prev, [offer.questionId]: true }));
      handleOf(offer.questionId).current?.focus();
      return;
    }
    onStart(offer, choice);
  }

  return (
    <div className="space-y-3">
      {views.map(({ offer, status, choice }) => {
        const options = offer.prediction?.options;
        return (
          <div
            key={offer.questionId}
            role="group"
            aria-label={t("practice.contrast.title")}
            className="rounded-lg border border-accent-300 bg-accent-50 px-3 py-2 text-sm text-ink-800 dark:border-accent-700 dark:bg-accent-950 dark:text-ink-100"
          >
            <p className="flex flex-wrap items-center gap-2 font-medium">
              <GitCompareArrows size={16} aria-hidden="true" className="shrink-0" />
              {t("practice.contrast.title")}
              <Badge tone="accent">{t("practice.contrast.badge")}</Badge>
            </p>
            <p className="mt-1 text-ink-700 dark:text-ink-200">{t("practice.contrast.hint")}</p>
            <p className="mt-1">
              <span className="font-medium">{t("practice.contrast.variesLabel")}</span>{" "}
              <Localized value={offer.varies} variant="inline" />
            </p>

            {status === "open" && (
              <div className="mt-3 space-y-3">
                {offer.prediction && (
                  <PredictionCard
                    ref={handleOf(offer.questionId)}
                    prompt={offer.prediction.prompt}
                    options={options}
                    value={choices[offer.questionId] ?? null}
                    onChange={(next) => {
                      setChoices((prev) => ({ ...prev, [offer.questionId]: next }));
                      setMissing((prev) => ({ ...prev, [offer.questionId]: false }));
                    }}
                    onSubmit={() => start(offer)}
                    error={
                      missing[offer.questionId] ? t("practice.prediction.required") : undefined
                    }
                  />
                )}
                <Button
                  variant="secondary"
                  className="max-sm:min-h-11"
                  onClick={() => start(offer)}
                >
                  {t("practice.contrast.start")}
                </Button>
              </div>
            )}
            {/* Always in the page, so the insertion is announced when the text arrives. */}
            <p
              role="status"
              className={status === "added" ? "mt-2 text-ink-700 dark:text-ink-200" : ""}
            >
              {status === "added" && (
                <>
                  {t("practice.contrast.added")}
                  {choice !== undefined && (
                    <>
                      {" "}
                      {t("practice.prediction.label")}:{" "}
                      <PredictionChoice choice={choice} options={options} />.
                    </>
                  )}
                </>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
}
